import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './FloatingAdminButton.css';

const FloatingAdminButton = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const navigate = useNavigate();

  // Auto-hide after 5 seconds of inactivity
  useEffect(() => {
    if (isExpanded) {
      // Set new timeout to hide after 5 seconds
      const timeout = setTimeout(() => {
        setIsHidden(true);
      }, 5000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isExpanded]);

  const handleToggle = () => {
    if (isHidden) {
      setIsHidden(false);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleAdminLogin = () => {
    navigate('/shesvla');
    setIsExpanded(false);
    setIsHidden(true);
  };

  const handleDashboardAccess = () => {
    // Check if user is authenticated before allowing dashboard access
    const token = localStorage.getItem('ntf_admin_token');
    if (token === 'authenticated') {
      // User is authenticated, allow access to dashboard
      navigate('/shesvla/dashboard');
      setIsExpanded(false);
      setIsHidden(true);
    } else {
      // User is not authenticated, redirect to login
      navigate('/shesvla');
      setIsExpanded(false);
      setIsHidden(true);
    }
  };

  const handleMouseEnter = () => {
    setIsHidden(false);
    setIsExpanded(true);
  };

  const handleMouseLeave = () => {
    // Set timeout to hide after 3 seconds when mouse leaves
    // eslint-disable-next-line no-unused-vars
    setTimeout(() => {
      setIsHidden(true);
      setIsExpanded(false);
    }, 3000);
  };

  return (
    <motion.div
      className="floating-admin-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Hidden state - half off screen */}
      <AnimatePresence>
        {isHidden ? (
          <motion.button
            key="hidden"
            className="floating-admin-button hidden"
            onClick={handleToggle}
            initial={{ x: 100 }}
            animate={{ x: 60 }}
            exit={{ x: 100 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            whileHover={{ x: 40 }}
            whileTap={{ scale: 0.95 }}
            title="Click to reveal admin panel"
          >
            <span className="button-icon">🔐</span>
          </motion.button>
        ) : (
          <motion.div
            key="expanded"
            className="floating-admin-menu"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: 'spring', stiffness: 400, damping: 30 }}
          >
            {/* Main button */}
            <motion.button
              className="floating-admin-button main"
              onClick={handleToggle}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              title="Close admin menu"
            >
              <span className="button-icon">🔐</span>
            </motion.button>

            {/* Admin Login button */}
            <motion.button
              className="floating-admin-item login-btn"
              onClick={handleAdminLogin}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: 0.1 }}
              whileHover={{ x: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Go to admin login"
            >
              <span className="item-icon">🔑</span>
              <span className="item-label">Login</span>
            </motion.button>

            {/* Dashboard button */}
            <motion.button
              className="floating-admin-item dashboard-btn"
              onClick={handleDashboardAccess}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: 0.15 }}
              whileHover={{ x: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Go to admin dashboard (requires login)"
            >
              <span className="item-icon">📊</span>
              <span className="item-label">Dashboard</span>
            </motion.button>

            {/* Close button */}
            <motion.button
              className="floating-admin-item close-btn"
              onClick={() => {
                setIsExpanded(false);
                setIsHidden(true);
              }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: 0.2 }}
              whileHover={{ x: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title="Hide admin menu"
            >
              <span className="item-icon">✕</span>
              <span className="item-label">Close</span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default FloatingAdminButton;
